import React, { FC, useMemo, useEffect, useRef } from "react";
import cn from "clsx";
import sortBy from "lodash/sortBy";
import SizeOptionList from "./SizeOptionList";
import SizeFormatSelectMob from "./SizeFormatSelectMob";
import { FilterOption } from "../../types";
import { useSizeFilterHelpers } from "../../hooks/useSizeFilterHelpers";

interface SizeAdvancedViewProps {
  group: any;
  searchQuery: string;
  selectedKeys: string[];
  onOptionToggle: (option: FilterOption, event: React.MouseEvent) => void;
  activeTabKey: string | null;
  onTabChange: (key: string) => void;
  activeSubgroupKey: string | null;
  onSubgroupChange: (key: string) => void;
  selectTitle?: string;
  onLeafOptionsChange?: (options: FilterOption[]) => void;
}

const SizeAdvancedView: FC<SizeAdvancedViewProps> = ({
  group,
  searchQuery,
  selectedKeys,
  onOptionToggle,
  activeTabKey,
  onTabChange,
  activeSubgroupKey,
  onSubgroupChange,
  selectTitle = "Select",
  onLeafOptionsChange
}) => {
  const {
    getUniqueOptionsByKey,
    filterOptionsWithValidLabel,
    getDeepestChildrenOptions
  } = useSizeFilterHelpers();

  const tabs = useMemo(() => {
    if (!group?.children?.length) return [];
    return sortBy(group.children, ["sortOrder"]);
  }, [group]);

  const selectedKeySet = useMemo(() => new Set(selectedKeys), [selectedKeys]);

  const selectedCountByTabKey = useMemo(() => {
    if (tabs.length === 0) return new Map<string, number>();
    const counts = new Map<string, number>();
    tabs.forEach((tab: any) => {
      const tabLeafOptions = getDeepestChildrenOptions(tab);
      const count = tabLeafOptions.reduce((total, option) => {
        if (!option?.key) return total;
        return selectedKeySet.has(option.key) ? total + 1 : total;
      }, 0);
      counts.set(tab.key, count);
    });
    return counts;
  }, [tabs, getDeepestChildrenOptions, selectedKeySet]);

  const selectedTab = useMemo(() => {
    if (tabs.length === 0) return null;
    return tabs.find((tab: any) => tab.key === activeTabKey) || tabs[0];
  }, [tabs, activeTabKey]);

  useEffect(() => {
    if (tabs.length === 0) return;
    const isCurrentTabValid = tabs.some((tab: any) => tab.key === activeTabKey);
    if (!isCurrentTabValid && tabs[0]?.key) {
      onTabChange(tabs[0].key);
    }
  }, [tabs, activeTabKey, onTabChange]);

  const subgroups = useMemo(() => {
    if (!selectedTab?.children?.length) return [];
    return sortBy(selectedTab.children, ["sortOrder"]);
  }, [selectedTab]);

  const selectedSubgroup = useMemo(() => {
    if (subgroups.length === 0) return null;
    return (
      subgroups.find((subgroup: any) => subgroup.key === activeSubgroupKey) ||
      subgroups[0]
    );
  }, [subgroups, activeSubgroupKey]);

  useEffect(() => {
    if (subgroups.length === 0) return;
    const isCurrentSubgroupValid = subgroups.some(
      (subgroup: any) => subgroup.key === activeSubgroupKey
    );
    if (!isCurrentSubgroupValid && subgroups[0]?.key) {
      onSubgroupChange(subgroups[0].key);
    }
  }, [subgroups, activeSubgroupKey, onSubgroupChange]);

  const leafOptions = useMemo(() => {
    if (!selectedSubgroup) return [];

    let options = getDeepestChildrenOptions(selectedSubgroup);

    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      options = options.filter(option =>
        option.label?.toLowerCase().includes(searchLower)
      );
    }

    return sortBy(filterOptionsWithValidLabel(getUniqueOptionsByKey(options)), [
      "sortOrder"
    ]);
  }, [
    selectedSubgroup,
    getDeepestChildrenOptions,
    searchQuery,
    getUniqueOptionsByKey,
    filterOptionsWithValidLabel
  ]);

  const prevLeafOptionsRef = useRef<FilterOption[]>([]);
  const onLeafOptionsChangeRef = useRef(onLeafOptionsChange);
  onLeafOptionsChangeRef.current = onLeafOptionsChange;

  useEffect(() => {
    const prevKeys = prevLeafOptionsRef.current.map(o => o.key).join(",");
    const currentKeys = leafOptions.map(o => o.key).join(",");

    if (prevKeys !== currentKeys) {
      prevLeafOptionsRef.current = leafOptions;
      onLeafOptionsChangeRef.current?.(leafOptions);
    }
  }, [leafOptions]);

  return (
    <div className="size_adv_container">
      <div className="size_adv_tabs">
        {tabs.map((tab: any) => (
          <div
            key={tab.key}
            className={cn("size_adv_tab", {
              active: activeTabKey === tab.key || selectedTab?.key === tab.key
            })}
            onClick={event => {
              event.stopPropagation();
              onTabChange(tab.key);
            }}
          >
            {tab.label}
            {selectedCountByTabKey.get(tab.key) ? (
              <span> ({selectedCountByTabKey.get(tab.key)})</span>
            ) : null}
          </div>
        ))}
      </div>

      <SizeFormatSelectMob
        options={subgroups.map(subgroup => ({
          key: subgroup.key,
          label: subgroup.label
        }))}
        value={activeSubgroupKey || selectedSubgroup?.key || ""}
        onChange={onSubgroupChange}
        title={selectTitle}
      />

      <SizeOptionList
        options={leafOptions}
        selectedKeys={selectedKeys}
        onOptionToggle={onOptionToggle}
      />
    </div>
  );
};

export default SizeAdvancedView;
